import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Mail, Plus, X } from 'lucide-react'

interface StepInviteMembersProps {
  loading: boolean
  error: string | null
  onSendInvites: (emails: string[]) => void | Promise<void>
  onSkip: () => void
}

export function StepInviteMembers({ loading, error, onSendInvites, onSkip }: StepInviteMembersProps) {
  const [emails, setEmails] = useState<string[]>([''])

  const addEmail = () => {
    if (emails.length < 14) {
      setEmails([...emails, ''])
    }
  }

  const removeEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index))
  }

  const updateEmail = (index: number, value: string) => {
    const updated = [...emails]
    updated[index] = value
    setEmails(updated)
  }

  const validEmails = emails.filter(e => e.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()))

  const handleSend = async () => {
    if (validEmails.length === 0) return
    await onSendInvites(validEmails)
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Invite your friends</h2>
      <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-6">
        Add email addresses for up to 14 additional players. They'll receive an invite link to join your league.
      </p>

      <div className="space-y-2 mb-4">
        <Label>Email addresses</Label>
        {emails.map((email, index) => (
          <div key={index} className="flex gap-2">
            <Input
              type="email"
              placeholder="friend@example.com"
              value={email}
              onChange={(e) => updateEmail(index, e.target.value)}
              autoFocus={index === emails.length - 1 && emails.length > 1}
            />
            {emails.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeEmail(index)}
                className="shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {emails.length < 14 && (
        <button
          type="button"
          onClick={addEmail}
          className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 mb-6"
        >
          <Plus className="w-4 h-4" />
          Add another email
        </button>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onSkip} disabled={loading}>
          Skip for now
        </Button>
        <Button onClick={handleSend} className="flex-1" disabled={loading || validEmails.length === 0}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending invites...
            </>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Send {validEmails.length > 0 ? `${validEmails.length} ` : ''}Invite{validEmails.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
